
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Wrench, RefreshCw, Save, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AdminSiteConfigSystemSettings = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  const systemConfigs = [
    { 
      key: 'maintenance_mode', 
      label: 'Maintenance Mode', 
      type: 'boolean',
      description: 'Enable site-wide maintenance mode'
    },
    { 
      key: 'maintenance_message', 
      label: 'Maintenance Message', 
      type: 'input',
      placeholder: 'Site is under maintenance. Please check back soon.'
    },
    { 
      key: 'api_rate_limit', 
      label: 'API Rate Limit (per minute)', 
      type: 'number',
      placeholder: '100'
    },
    { 
      key: 'max_file_upload_size', 
      label: 'Max Upload Size (MB)', 
      type: 'number',
      placeholder: '10'
    },
    { 
      key: 'default_image_quality', 
      label: 'Default Image Quality', 
      type: 'input',
      placeholder: 'standard'
    },
    { 
      key: 'max_images_per_batch', 
      label: 'Max Images Per Batch', 
      type: 'number',
      placeholder: '4'
    },
    { 
      key: 'enable_batch_generation', 
      label: 'Enable Batch Generation', 
      type: 'boolean',
      description: 'Allow users to generate multiple images at once'
    },
    { 
      key: 'image_storage_days', 
      label: 'Image Storage Days', 
      type: 'number',
      placeholder: '30'
    }
  ];

  const SystemConfigCard = ({ config }: { config: typeof systemConfigs[0] }) => {
    const { value, loading, refetch } = useSiteConfigValue(config.key);
    const [localValue, setLocalValue] = useState('');
    const [hasChanged, setHasChanged] = useState(false);

    React.useEffect(() => {
      if (value !== null && value !== localValue) {
        setLocalValue(value);
        setHasChanged(false);
      }
    }, [value]);

    const handleInputChange = (newValue: string) => {
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || ''));
    };

    const handleBooleanChange = (checked: boolean) => {
      const newValue = checked.toString();
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || 'false'));
    };

    const handleUpdate = async () => {
      setSaving(config.key);
      try {
        console.log(`Updating ${config.key} to:`, localValue);
        
        const { error } = await supabase.rpc('update_site_config', {
          config_key: config.key,
          new_value: localValue,
          admin_email: null,
          change_reason: `Updated ${config.label} via Admin Panel`
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: `${config.label} updated successfully`,
        });
        
        setHasChanged(false);
        await refetch();
      } catch (error) {
        console.error(`Error updating ${config.key}:`, error);
        toast({
          title: "Error",
          description: `Failed to update ${config.label}`,
          variant: "destructive",
        });
      } finally {
        setSaving(null);
      }
    };

    const handleRefresh = async () => {
      console.log(`Refreshing ${config.key}...`);
      await refetch();
    };

    const isCritical = config.key === 'maintenance_mode' && localValue === 'true';

    return (
      <Card className={`bg-white/10 backdrop-blur-md border-white/20 ${isCritical ? 'border-red-500/30' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm font-medium flex items-center justify-between">
            <span className="flex items-center space-x-2">
              {config.label}
              {isCritical && <AlertTriangle className="w-4 h-4 text-red-400" />}
            </span>
            <div className="flex items-center space-x-2">
              {loading ? (
                <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white text-xs">
                {config.type === 'boolean' ? 'Status' : 'Current Value'}
              </Label>
              <div className="flex items-center space-x-1">
                {hasChanged && <Badge className="bg-orange-600/20 text-orange-400 text-xs">Modified</Badge>}
                {isCritical && <Badge className="bg-red-600/20 text-red-400 text-xs">Critical</Badge>}
                {config.type === 'boolean' ? (
                  localValue === 'true' ? (
                    <CheckCircle className="w-3 h-3 text-green-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-gray-400" />
                  )
                ) : (
                  localValue ? (
                    <CheckCircle className="w-3 h-3 text-green-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-gray-400" />
                  )
                )}
              </div>
            </div>
            
            {config.type === 'boolean' ? (
              <div className="flex items-center space-x-3">
                <Switch
                  checked={localValue === 'true'}
                  onCheckedChange={handleBooleanChange}
                  disabled={saving === config.key}
                />
                <span className="text-white text-sm">
                  {localValue === 'true' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ) : (
              <Input
                type={config.type === 'number' ? 'number' : 'text'}
                value={localValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={saving === config.key}
                className="bg-gray-800 border-gray-600 text-white text-sm"
              />
            )}
            
            {config.description && (
              <p className="text-xs text-gray-400">{config.description}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {loading ? 'Loading...' : `Loaded: ${value || 'Not set'}`}
            </div>
            <Button
              onClick={handleUpdate}
              disabled={!hasChanged || saving === config.key}
              size="sm"
              className={`${isCritical ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white disabled:opacity-50`}
            >
              {saving === config.key ? (
                <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin mr-1" />
              ) : (
                <Save className="w-3 h-3 mr-1" />
              )}
              Update
            </Button>
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            <div>Debug Info:</div>
            <div>• Key: {config.key}</div>
            <div>• Current: {value || 'null'}</div>
            <div>• Local: {localValue || 'empty'}</div>
            <div>• Changed: {hasChanged ? 'yes' : 'no'}</div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <Wrench className="w-5 h-5" />
          <span>System & Maintenance Configuration</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systemConfigs.map((config) => (
            <SystemConfigCard key={config.key} config={config} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigSystemSettings;
